/**
 * Billing Service
 * Handles subscription and billing operations with Stripe integration
 */
import { accountApi as apiService } from './api';
import { getPlanById } from '../constants/plans';

class BillingService {
  /**
   * Get current user's subscription and billing information
   * @returns {Promise} Billing information
   */
  async getBillingInfo() {
    try {
      const response = await apiService.request('/billing/info');
      return {
        subscription: response.subscription || null,
        paymentMethod: response.paymentMethod || null,
        invoices: response.invoices || [],
        activeDiscount: response.activeDiscount || null,
        nextInvoice: response.nextInvoice || null,
        subscriptionItems: response.subscriptionItems || [],
        usage: response.usage || {
          tokens: 0,
          integrations: 0,
          storage: 0,
          dailyUsage: {}
        }
      };
    } catch (error) {
      console.error('Error fetching billing info:', error);
      // Return default trial plan if no subscription found
      return {
        subscription: {
          id: null,
          planId: 'trial',
          status: 'active',
          currentPeriodStart: new Date().toISOString(),
          currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          cancelAtPeriodEnd: false
        },
        paymentMethod: null,
        invoices: [],
        activeDiscount: null,
        nextInvoice: null,
        subscriptionItems: [],
        usage: {
          tokens: 0,
          integrations: 0,
          storage: 0,
          dailyUsage: {}
        }
      };
    }
  }

  /**
   * Create a Stripe checkout session for subscription
   * @param {string} planId - Plan ID to subscribe to
   * @returns {Promise} Checkout session URL
   */
  async createCheckoutSession(planId) {
    const plan = getPlanById(planId);
    if (!plan) {
      throw new Error('Invalid plan selected');
    }

    if (plan.price === 0) {
      // Handle free trial - no Stripe needed
      return await this.activateTrialPlan();
    }

    try {
      const response = await apiService.request('/billing/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify({
          planId: planId,
        })
      });

      return response.checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Activate trial plan (free)
   * @returns {Promise} Activation result
   */
  async activateTrialPlan() {
    try {
      const response = await apiService.request('/billing/activate-trial', {
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error('Error activating trial:', error);
      throw error;
    }
  }

  /**
   * Cancel subscription
   * @param {boolean} cancelAtPeriodEnd - Whether to cancel at period end or immediately
   * @returns {Promise} Cancellation result
   */
  async cancelSubscription(cancelAtPeriodEnd = true) {
    try {
      const response = await apiService.request('/billing/cancel-subscription', {
        method: 'POST',
        body: JSON.stringify({
          cancelAtPeriodEnd
        })
      });
      return response;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  /**
   * Reactivate a cancelled subscription
   * @returns {Promise} Reactivation result
   */
  async reactivateSubscription() {
    try {
      const response = await apiService.request('/billing/reactivate-subscription', {
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  }

  /**
   * Update payment method
   * @returns {Promise} Update payment method URL
   */
  async updatePaymentMethod() {
    try {
      const response = await apiService.request('/billing/update-payment-method', {
        method: 'POST'
      });
      return response.updateUrl;
    } catch (error) {
      console.error('Error updating payment method:', error);
      throw error;
    }
  }

  /**
   * Get customer portal URL for managing subscription
   * @returns {Promise} Portal URL
   */
  async getCustomerPortalUrl() {
    try {
      const response = await apiService.request('/billing/customer-portal', {
        method: 'POST'
      });
      return response.portalUrl;
    } catch (error) {
      console.error('Error getting customer portal:', error);
      throw error;
    }
  }

  /**
   * Download invoice
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} Invoice download URL
   */
  async downloadInvoice(invoiceId) {
    try {
      const response = await apiService.request(`/billing/invoices/${invoiceId}/download`);
      return response.downloadUrl;
    } catch (error) {
      console.error('Error downloading invoice:', error);
      throw error;
    }
  }

  /**
   * Apply a Stripe coupon / promotion code to the current account.
   * If the user already has a Stripe customer it's applied immediately;
   * otherwise it's stashed and used on next checkout.
   */
  async applyCoupon(code) {
    const response = await apiService.request('/billing/coupon/apply', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    return response;
  }

  async processStripeSuccess(sessionId) {
    try {
      const response = await apiService.request('/billing/process-success', {
        method: 'POST',
        body: JSON.stringify({ sessionId })
      });
      return response;
    } catch (error) {
      console.error('Error processing Stripe success:', error);
      throw error;
    }
  }

  /**
   * Format subscription for display
   * @param {Object} subscription - Raw subscription data
   * @returns {Object} Formatted subscription
   */
  formatSubscription(subscription) {
    if (!subscription) return null;

    const plan = getPlanById(subscription.planId);
    if (!plan) return null;

    return {
      id: subscription.id,
      plan: plan,
      status: subscription.status,
      currentPeriodStart: new Date(subscription.currentPeriodStart),
      currentPeriodEnd: new Date(subscription.currentPeriodEnd),
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
      nextBillingDate: subscription.cancelAtPeriodEnd ? null : new Date(subscription.currentPeriodEnd)
    };
  }

  /**
   * Format usage data for display
   * @param {Object} usage - Raw usage data
   * @param {Object} plan - Current plan
   * @returns {Object} Formatted usage
   */
  formatUsage(usage, plan) {
    return {
      tokens: {
        used: usage.tokens || 0,
        limit: plan?.tokens || 0,
        percentage: plan?.tokens ? Math.round(((usage.tokens || 0) / plan.tokens) * 100) : 0
      },
      integrations: {
        used: usage.integrations || 0,
        limit: 'Unlimited',
        percentage: 0
      }
    };
  }
}

const billingService = new BillingService();
export default billingService;
